import { ref, computed } from 'vue'
import { useMatchStore } from '@/stores/match.js'
import { useToast } from '@/composables/useToast.js'
import { useTranslationsStore } from '@/stores/translations.js'

export function useTeamManagement() {
    const matchStore = useMatchStore()
    const { addToast: toast } = useToast()
    const { t } = useTranslationsStore()

    const playMode = computed({
        get: () => matchStore.playMode || 'singles',
        set: (value) => matchStore.setPlayMode(value),
    })
    const team1UUID = computed({
        get: () => matchStore.team1UUID,
        set: (value) => {
            matchStore.team1UUID = value
        },
    })
    const team2UUID = computed({
        get: () => matchStore.team2UUID,
        set: (value) => {
            matchStore.team2UUID = value
        },
    })
    const isUpdating = ref(false)

    const showTeamModal = ref(false)
    const showModeChangeConfirmation = ref(false)
    const selectedPlayerForTeam = ref(null)
    const modalMode = ref('player-to-team')
    const selectedTeamForPlayers = ref(null)
    const pendingModeChange = ref(null)

    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0
            const v = c == 'x' ? r : (r & 0x3) | 0x8
            return v.toString(16)
        })
    }

    const profiles = computed(() => matchStore.setup.profiles || [])

    const isTeamMode = computed(() => playMode.value === 'teams')

    const hasExistingTeams = computed(() => {
        return matchStore.isTeamPlay
    })

    const teamGroups = computed(() => {
        const groups = {}
        profiles.value.forEach((profile) => {
            if (profile.team) {
                if (!groups[profile.team]) {
                    groups[profile.team] = []
                }
                groups[profile.team].push(profile)
            }
        })
        return groups
    })

    const existingTeamUUIDs = computed(() => {
        if (!matchStore.isTeamPlay) return []
        return Object.keys(teamGroups.value).filter((teamId) => teamGroups.value[teamId].length > 1)
    })

    const getPlayersForTeam = (teamUUID) => {
        return teamUUID ? teamGroups.value[teamUUID] || [] : []
    }

    const team1Players = computed(() => getPlayersForTeam(matchStore.team1UUID))
    const team2Players = computed(() => getPlayersForTeam(matchStore.team2UUID))

    const unassignedPlayers = computed(() => {
        if (!isTeamMode.value) return []

        const assignedTeamUUIDs = [matchStore.team1UUID, matchStore.team2UUID].filter(Boolean)
        return profiles.value.filter(
            (profile) => !profile.team || !assignedTeamUUIDs.includes(profile.team),
        )
    })

    const assignedPlayersCount = computed(() => {
        return isTeamMode.value ? team1Players.value.length + team2Players.value.length : 0
    })

    const createTeamStats = (players) => ({
        players,
        readyCount: players.filter((p) => p.ready).length,
        totalCount: players.length,
    })

    const teamStats = computed(() => ({
        team1: createTeamStats(team1Players.value),
        team2: createTeamStats(team2Players.value),
        unassigned: {
            players: unassignedPlayers.value,
            count: unassignedPlayers.value.length,
        },
    }))

    const createTeamStructure = () => {
        const existing = existingTeamUUIDs.value

        if (existing.length >= 2) {
            matchStore.setTeamUUIDs(existing[0], existing[1])
        } else if (existing.length === 1) {
            matchStore.setTeamUUIDs(existing[0], generateUUID())
        } else {
            matchStore.setTeamUUIDs(generateUUID(), generateUUID())
        }
    }

    const clearTeamStructure = () => {
        matchStore.clearTeamUUIDs()
    }

    const getTeamUUID = (teamNumber) => {
        return teamNumber === 1 ? matchStore.team1UUID : matchStore.team2UUID
    }

    const executeWithUpdateLock = async (operation) => {
        if (isUpdating.value) return false
        isUpdating.value = true
        try {
            await operation()
            return true
        } finally {
            isUpdating.value = false
        }
    }

    const assignPlayerToTeam = async (player, teamUUID) => {
        return executeWithUpdateLock(async () => {
            await matchStore.updatePlayerTeam(player.id, teamUUID)
        })
    }

    const removePlayerFromTeam = async (player) => {
        return executeWithUpdateLock(async () => {
            await matchStore.updatePlayerTeam(player.id, null)
        })
    }

    const updateAllPlayersTeam = async (getTeamUUID) => {
        const teamUpdates = []

        for (const profile of profiles.value) {
            const teamUUID = typeof getTeamUUID === 'function' ? getTeamUUID() : getTeamUUID
            teamUpdates.push({ profileId: profile.id, teamUUID })
        }

        if (teamUpdates.length > 0) {
            await matchStore.updatePlayersTeams(teamUpdates)
        }
    }

    const removeAllTeamAssignments = async () => {
        await updateAllPlayersTeam(null)
        clearTeamStructure()
    }

    const assignUniqueTeamUUIDsToAllPlayers = async () => {
        await updateAllPlayersTeam(() => generateUUID())
    }

    const switchPlayMode = async (mode) => {
        if (isUpdating.value) return false

        const previousMode = playMode.value
        isUpdating.value = true
        try {
            matchStore.setPlayMode(mode)

            if (mode === 'teams' && previousMode !== 'teams') {
                // Wipe all existing team assignments so players can be properly assigned to Team 1/2
                await removeAllTeamAssignments()
                createTeamStructure()
            } else if (mode === 'singles' && previousMode === 'teams') {
                // Give each player their own unique team UUID
                await assignUniqueTeamUUIDsToAllPlayers()
                clearTeamStructure()
            }
            return true
        } catch (error) {
            console.error('Failed to set play mode:', error)
            matchStore.setPlayMode(previousMode)
            throw error
        } finally {
            isUpdating.value = false
        }
    }

    const openTeamAssignmentModal = (player) => {
        modalMode.value = 'player-to-team'
        selectedPlayerForTeam.value = player
        selectedTeamForPlayers.value = null
        showTeamModal.value = true
    }

    const openPlayerSelectionModal = (teamNumber, unassignedPlayersList) => {
        if (unassignedPlayersList.length === 0) {
            toast(t('general.something_went_wrong'), 5000, t('player_selector.error_no_unassigned_players'))
            return false
        }

        modalMode.value = 'team-to-player'
        selectedPlayerForTeam.value = null
        selectedTeamForPlayers.value = teamNumber
        showTeamModal.value = true
        return true
    }

    const closeTeamModal = () => {
        showTeamModal.value = false
        selectedPlayerForTeam.value = null
        selectedTeamForPlayers.value = null
    }

    const openModeChangeConfirmation = (newMode) => {
        pendingModeChange.value = newMode
        showModeChangeConfirmation.value = true
    }

    const closeModeChangeConfirmation = () => {
        showModeChangeConfirmation.value = false
        pendingModeChange.value = null
    }

    const showValidation = () => {
        matchStore.setShowValidationError(true)
    }

    const hideValidation = () => {
        matchStore.setShowValidationError(false)
    }

    const initializeTeams = () => {
        if (hasExistingTeams.value) {
            matchStore.setPlayMode('teams')
            createTeamStructure()
        }
    }

    const showValidationError = computed(() => matchStore.showValidationError)

    return {
        playMode,
        team1UUID,
        team2UUID,
        isUpdating,
        showTeamModal,
        showModeChangeConfirmation,
        selectedPlayerForTeam,
        modalMode,
        selectedTeamForPlayers,
        pendingModeChange,

        profiles,
        isTeamMode,
        hasExistingTeams,
        teamGroups,
        team1Players,
        team2Players,
        unassignedPlayers,
        assignedPlayersCount,
        teamStats,

        createTeamStructure,
        clearTeamStructure,
        getTeamUUID,
        assignPlayerToTeam,
        removePlayerFromTeam,
        removeAllTeamAssignments,
        assignUniqueTeamUUIDsToAllPlayers,
        switchPlayMode,
        initializeTeams,
        openTeamAssignmentModal,
        openPlayerSelectionModal,
        closeTeamModal,
        openModeChangeConfirmation,
        closeModeChangeConfirmation,
        showValidation,
        hideValidation,
        showValidationError,
    }
}
